import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { ArrowBigIcon } from '../../Icons/ArrowBigIcon'
import ExpandedArticle from './ExpandedArticle'
import useStores from '../../../stores/useStore'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { InputGroup, Form } from 'react-bootstrap'
import { DeleteIcon } from '../../Icons/DeleteIcon'
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'
import { t } from 'i18next'
import { BiBarcodeReader } from 'react-icons/bi'
import CreateRFIDInstance from '../CreateRFIDInstance'
import { colors } from '../../../styles/colors'

const StyledTr = styled.tr`
  &:hover {
    background-color: rgba(0, 0, 0, 0.075);
  }
  border-top: ${(props) => (props.borderTop ? '1px solid #e0e0e0' : '')};
`
const StyledProductImgWrap = styled.div`
  //box-shadow: 0 0 15px rgb(0 0 0 / 20%);
  background:white;
  width: 50px;
  height:  50px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 15px 0 15px;
  svg {
    width: 100%;
    height: 100%;
    opacity: 0.3;
  }
`
const StyledProductImg = styled.img`
  max-width: 100%;
  max-height: 100%;
`

const SelectedAmountsRow = ({ article, position }) => {
  const { inventoryStore } = useStores()
  const [open, setOpen] = useState(true)
  const { actionType } = useParams()
  const [instances, setInstances] = useState([])
  const [openBasketAmount, setOpenBasketAmount] = useState(null)
  const [openCreateRFIDModal, setOpenCreateRFIDModal] = useState(false)
  const [zeroAmountIsConfirmed, setZeroAmountIsConfirmed] = useState(false)

  const init = async () => {
    if (actionType === 'assets') {
      let variantId = position.articleVariant
        ? position.articleVariant.id
        : null
      let params = variantId ? { variantId } : null
      let basketArticleAmounts = await apiClient.getJson(
        APIEndpoints.businesspartnerShoppingArticleAmounts(
          inventoryStore.employeeId,
          'asset',
          article.id
        ),
        params
      )
      if (basketArticleAmounts) {
        setOpenBasketAmount(basketArticleAmounts)
      }
    }
    loadInstances()
  }
  const loadInstances = useCallback(async () => {
    if (
      (actionType === 'inventoryBookings' && !position.negativeAmount) ||
      !position.rows
    ) {
      return null
    }
    let newInstances = await inventoryStore.getInstances(
      article.article.id,
      position.articleVariant
    )
    if (
      !inventoryStore.scanModus &&
      position.rows && newInstances.length === position.rows.length
    ) {
      for (let [index, instance] of Object.entries(newInstances)) {
        if (index > position.rows.length - 1) {
          break
        }
        let alreadyExists = inventoryStore.instanceAlreadyExists(instance)
        if (!alreadyExists) {
          inventoryStore.updateInstances(
            article,
            position.articleVariant,
            instance,
            index
          )
        }
      }
    }

    setInstances(newInstances)
  }, [actionType, article, position, inventoryStore])
  useEffect(() => {
    loadInstances()
  }, [inventoryStore.instances, inventoryStore.neutralizedRFIDs, loadInstances])
  useMountEffect(() => {
    init()
  })
  const getPendingDeliveryAmount = (item) => {
    let basketQuantity = openBasketAmount || 0
    let orderedQuantity = item.orderedQuantity || 0
    let shippedQuantity = item.shippedQuantity || 0
    return basketQuantity + orderedQuantity - shippedQuantity
  }

  const onChangeAssetQuantity = (position) => (e) => {
    let newAmount = parseInt(e.target.value) || 0
    if (newAmount >= 0) {
      inventoryStore.updateRows(article, position.articleVariant, newAmount)
      setZeroAmountIsConfirmed(position.zeroAmountIsConfirmed)
    }
  }
  const handleDelete = (position) => {
    inventoryStore.deleteVariant(article, position.articleVariant)
  }
  const getAmountInputStyle = (position) => {
    let currentAmount =
      position.rows && position.rows.length ? position.rows.length : 0
    if (currentAmount === 0 && !zeroAmountIsConfirmed) {
      return { color: colors.black }
    } else if (currentAmount === position.currentQuantity) {
      return { color: colors.white, backgroundColor: colors.green }
    } else if (currentAmount >= position.currentQuantity) {
      return { color: colors.black, backgroundColor: colors.yellow }
    } else {
      return { color: colors.white, backgroundColor: colors.red }
    }
  }
  const renderAmount = () => {
    if (actionType === 'assets') {
      return (
        <div className='d-flex'>
          {inventoryStore.scanModus && (
            <div>
              {position.rows && position.rows.length ? position.rows.length : 0}
              /{position.currentQuantity}&nbsp;
              {getPendingDeliveryAmount(position) === 0
                ? null
                : `(${getPendingDeliveryAmount(position)}*)`}
            </div>
          )}
          {!inventoryStore.scanModus && (
            <>
              <InputGroup className='m-0' style={{ width: '200px' }}>
                <Form.Control
                  name={`${position.id}`}
                  type='number'
                  min='0'
                  //max={position.currentQuantity}
                  placeholder='Anzahl'
                  value={
                    position.rows && position.rows.length
                      ? position.rows.length
                      : zeroAmountIsConfirmed
                        ? 0
                        : ''
                  }
                  onChange={onChangeAssetQuantity(position)}
                />
                <InputGroup.Text
                  id='basic-addon2'
                  style={getAmountInputStyle(position)}
                >
                  /{position.currentQuantity}&nbsp;
                  {getPendingDeliveryAmount(position) === 0
                    ? null
                    : `(${getPendingDeliveryAmount(position)}*)`}
                </InputGroup.Text>
              </InputGroup>
              <div
                className='btn-extra-padding me-3'
                role='button'
                onClick={() => setOpenCreateRFIDModal(true)}
                data-toggle='tooltip'
                data-placement='right'
                title={t('inventory:generateScanCode2')}
              >
                <BiBarcodeReader />
              </div>
            </>
          )}
          {(inventoryStore.scanModus || !position.currentQuantity) && (
            <div
              className='btn-extra-padding'
              role='button'
              onClick={() => handleDelete(position)}
            >
              <DeleteIcon title={t('delete')} />
            </div>
          )}
        </div>
      )
    } else {
      return (
        <>
          {position.negativeAmount ? '-' : '+'}
          {position.rows.length}
        </>
      )
    }
  }
  const checkIsExpandable = useCallback(() => {
    if (actionType === 'assets') {
      let instancesExists = instances && instances.length
      return instancesExists
    }
    return true
  }, [actionType, instances])
  const maxQuantity = () => {
    return article.articleAvailability?.maxAmount ?? 0
  }
  const totalQuantity = () => {
    return article.positions.reduce(
      (acc, position) => acc + (position.rows?.length ?? 0),
      0
    )
  }
  const handleOpen = (e) => {
    const { cursor } = getComputedStyle(e.target)
    if (
      actionType === 'assets' &&
      (inventoryStore.scanModus || cursor === 'pointer' || cursor === 'text')
    ) {
      return false
    }
    setOpen((prev) => !prev)
  }
  return (
    <>
      <StyledTr
        borderTop
        onClick={(e) =>
          actionType !== 'assets' ? setOpen((prev) => !prev) : handleOpen(e)
        }
        aria-controls='example-collapse-text'
        role='button'
        aria-expanded={open}
        style={{ cursor: actionType !== 'assets' ? 'pointer' : 'default' }}
      >
        <td>
          {inventoryStore.actionType === 'assets' && (
            article.article.image ? <StyledProductImgWrap><StyledProductImg src={article.article.image.url} alt={article.article.displayName} /></StyledProductImgWrap> : null
          )}
        </td>
        <td>
          {actionType === 'returnBookings' ||
            actionType === 'articleTransfer' ||
            actionType === 'inventoryBookings' ||
            actionType === 'assetArticleTransfer' ||
            (checkIsExpandable() &&
              !inventoryStore.scanModus &&
              actionType === 'assets' &&
              position.rows?.length) ? (
            <ArrowBigIcon variant={open ? 'down' : 'right'} />
          ) : null}
          {article.article.displayName}
        </td>
        <td>{article.article.articleNumber}</td>
        <td>
          {position.articleVariant !== null &&
            position.articleVariant.displayName}
        </td>
        {inventoryStore.actionType === 'assets' && (
          <td>
            {maxQuantity() > 0 ? (
              <div
                style={{
                  color:
                    totalQuantity() > maxQuantity() ? colors.red : colors.green,
                }}
              >
                {totalQuantity()}/{maxQuantity()}
              </div>
            ) : (
              ''
            )}
          </td>
        )}
        <td>{renderAmount()}</td>
      </StyledTr>
      {checkIsExpandable() && open ? (
        <ExpandedArticle
          position={position}
          article={article}
          instances={instances}
        />
      ) : null}
      {openCreateRFIDModal && (
        <CreateRFIDInstance
          article={article}
          articleVariant={position.articleVariant}
          handleClose={() => {
            setOpenCreateRFIDModal(false)
          }}
        />
      )}
    </>
  )
}

export default SelectedAmountsRow
